import { sortBy as _sortBy } from 'lodash';

import type { AdType, DisplayEnum } from '../../../../../../configs';
import { getPermittedAdTypes } from '../../../../../../configs';
import type { AdModel } from '../../../../TraffickingPage/modelConverters';
import type { LineItemModel } from '../../../../TraffickingPage/modelConverters';
import type { DuplicateAd } from '../types';
import type { DuplicateLineItem, LineItemAdNames } from '../types';

export const getDuplicateAdsFromOriginalAds = (originalAds: AdModel[]): DuplicateAd[] => {
  return originalAds.map(
    (ad): DuplicateAd => {
      return {
        ...ad,
        selectedSections: [],
        isVisited: false,
      };
    }
  );
};

export const updateLineItemAdNames = (
  lineItemsToSetAdNames: DuplicateLineItem[],
  ads: AdModel[]
): DuplicateLineItem[] => {
  const adNames = ads.map((ad) => ({ id: ad.id, name: ad.name, indexToUse: 1 }));

  return lineItemsToSetAdNames.map((lineItemToSetAdName) => {
    if (!lineItemToSetAdName.selected || lineItemToSetAdName.conflicted) {
      lineItemToSetAdName.adNames = {};

      return lineItemToSetAdName;
    }

    const lineItemAdNames: LineItemAdNames = {};
    adNames.forEach((adName) => {
      if (lineItemToSetAdName.initiallyDeselectedForAdIds.includes(adName.id)) return;

      if (lineItemToSetAdName.adNames[adName.id] && lineItemToSetAdName.adNames[adName.id].editedName) {
        lineItemAdNames[adName.id] = lineItemToSetAdName.adNames[adName.id];
      } else {
        lineItemAdNames[adName.id] = {
          name: `${adName.name}_${adName.indexToUse}`,
        };
        adName.indexToUse++;
      }
    });

    lineItemToSetAdName.adNames = lineItemAdNames;

    return lineItemToSetAdName;
  });
};

export const getInitialDuplicatedLineItems = (
  selectedLineItems: LineItemModel[],
  originalDuplicatedAds: AdModel[],
  adType: DisplayEnum<AdType>
): DuplicateLineItem[] => {
  const sortedLineItemsByName = _sortBy(selectedLineItems, ['name']);

  if (!originalDuplicatedAds.length) {
    return [];
  }

  const lineItemIdsWithAdIds = new Map<string, string[]>();

  originalDuplicatedAds.forEach((originalDuplicatedAd) => {
    const lineItemId = originalDuplicatedAd.lineItem?.id;

    if (lineItemId) {
      const newAdIds = [...(lineItemIdsWithAdIds.get(lineItemId) ?? []), originalDuplicatedAd.id];

      lineItemIdsWithAdIds.set(lineItemId, newAdIds);
    }
  });

  const lineItemsList = sortedLineItemsByName.map((lineItem) => {
    const allowedLineItemAdTypes =
      lineItem.adProduct.key !== 'UNRECOGNIZED'
        ? getPermittedAdTypes({
            adProductKey: lineItem.adProduct?.key,
            displayPublisherTarget: lineItem.displayPublisherTarget,
          })
        : [];
    const isConflictedLineItem = !allowedLineItemAdTypes.some(
      (allowedLineItemAdType) => allowedLineItemAdType.key === adType.key
    );

    return {
      id: lineItem.id,
      name: lineItem.name,
      schedule: lineItem.schedule,
      startDate: lineItem.startDate,
      endDate: lineItem.endDate,
      adProduct: lineItem.adProduct,
      billableThirdParty: lineItem.billableThirdParty,
      isCoppaOrCaru: lineItem.isCoppaOrCaru,
      campaign: lineItem.campaign,
      adNames: {},
      selected: !isConflictedLineItem,
      conflicted: isConflictedLineItem,
      countries: lineItem.countries,
      initiallyDeselectedForAdIds: lineItemIdsWithAdIds.get(lineItem.id) ?? [],
      displayPublisherTarget: lineItem.displayPublisherTarget,
    };
  });

  return updateLineItemAdNames(lineItemsList, originalDuplicatedAds);
};

export const isDuplicateLineItemSelected = (
  selected: boolean,
  initiallyDeselectedForAdIds: string[],
  adId: string
): boolean => {
  return selected && !initiallyDeselectedForAdIds.includes(adId);
};
